import { validateSchema } from '@multimodal/web-sdk'
import actionCatalog from '../../../schemas/actions/catalog.json'
import actionSchemaDocument from '../../../schemas/actions/common.schema.json'
import { runFloodAnalysisStrict } from './hydroService.js'

const FROZEN_ACTION_NAMES = Object.freeze([
  'cesium.selectObject',
  'ui.openPanel',
  'flood.runAnalysis',
  'flood.showRange',
  'ui.clearAnalysis',
])

const registrationsByClient = new WeakMap()

function generateUndoToken(actionName) {
  return `${actionName}:undo:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`
}

function contractFor(name) {
  const contract = actionCatalog.actions.find(action => action.name === name)
  if (!contract) throw new Error(`Frozen action contract missing: ${name}`)
  return contract
}

function resolvedSchema(reference) {
  const prefix = './common.schema.json#/$defs/'
  const name = reference && reference.$ref && reference.$ref.startsWith(prefix)
    ? reference.$ref.slice(prefix.length)
    : ''
  const definition = actionSchemaDocument.$defs[name]
  if (!definition) throw new Error(`Frozen JSON Schema missing: ${name}`)
  return { ...definition, $defs: actionSchemaDocument.$defs }
}

function failure(code, message, retryable = false, details = {}) {
  return {
    success: false,
    summary: message,
    error: code,
    errorCode: code,
    output: {
      success: false,
      error: { code, message, retryable, details },
    },
  }
}

function failureFrom(error, fallbackCode) {
  return failure(error?.code || fallbackCode, error?.message || fallbackCode, Boolean(error?.retryable), {
    status: error?.status,
  })
}

function requireScene(getScene) {
  const scene = getScene()
  if (!scene) {
    const error = new Error('Cesium scene is not ready')
    error.code = 'SCENE_NOT_READY'
    throw error
  }
  return scene
}

export function createCompetitionActionRegistrations({
  getScene,
  runAnalysis = runFloodAnalysisStrict,
  tokenFactory = generateUndoToken,
}) {
  const undoRecords = new Map()
  const undoByTask = new Map()

  function saveUndo(actionName, restore, context = {}) {
    const token = context.undoToken || tokenFactory(actionName)
    const record = { actionName, taskId: context.taskId || '', restore, token }
    undoRecords.set(token, record)
    if (record.taskId) undoByTask.set(`${record.taskId}:${actionName}`, record)
    return token
  }

  async function consumeUndo(actionName, context) {
    const token = context && context.undoToken
    const taskKey = context && context.taskId ? `${context.taskId}:${actionName}` : ''
    const record = (token && undoRecords.get(token)) || (taskKey && undoByTask.get(taskKey))
    if (!record || record.actionName !== actionName) {
      const error = new Error(`Undo is unavailable for ${actionName}`)
      error.code = 'UNDO_UNAVAILABLE'
      throw error
    }
    await record.restore()
    undoRecords.delete(record.token)
    if (record.taskId) undoByTask.delete(`${record.taskId}:${actionName}`)
  }

  function registration(name, execute, undo) {
    const contract = contractFor(name)
    return {
      name,
      description: contract.description,
      inputSchema: resolvedSchema(contract.inputSchema),
      outputSchema: resolvedSchema(contract.outputSchema),
      riskLevel: contract.riskLevel,
      timeout: contract.timeout,
      execute,
      ...(undo ? { undo } : {}),
    }
  }

  const actions = [
    registration('cesium.selectObject', async (args, context) => {
      try {
        const scene = requireScene(getScene)
        const result = await scene.assistantSelectObject(args.target, args.locate !== false)
        const undoToken = saveUndo('cesium.selectObject', () => scene.assistantRestoreSelection(result.snapshot), context)
        const output = {
          success: true,
          selectedTarget: result.selectedTarget,
          located: Boolean(result.located),
          previousTarget: result.previousTarget || null,
          undoToken,
        }
        return { success: true, summary: `已选择 ${output.selectedTarget.displayName}`, output, undoToken }
      } catch (error) {
        return failureFrom(error, 'TARGET_NOT_FOUND')
      }
    }, context => consumeUndo('cesium.selectObject', context)),

    registration('ui.openPanel', async (args, context) => {
      try {
        const scene = requireScene(getScene)
        const result = await scene.assistantOpenPanel(args.panelId, args.focus !== false)
        const undoToken = saveUndo('ui.openPanel', () => scene.assistantRestorePanel(result.snapshot), context)
        const output = {
          success: true,
          panelId: 'flood-analysis',
          opened: Boolean(result.opened),
          wasAlreadyOpen: Boolean(result.wasAlreadyOpen),
          undoToken,
        }
        return { success: true, summary: '淹没分析面板已打开', output, undoToken }
      } catch (error) {
        return failureFrom(error, 'PANEL_OPEN_FAILED')
      }
    }, context => consumeUndo('ui.openPanel', context)),

    registration('flood.runAnalysis', async args => {
      try {
        const output = await runAnalysis(args)
        if (output && output.success === false) {
          return failure(
            output.error?.code || 'ANALYSIS_SERVICE_FAILED',
            output.error?.message || 'Flood analysis failed',
            Boolean(output.error?.retryable),
            output.error?.details || {},
          )
        }
        const contract = contractFor('flood.runAnalysis')
        const validation = validateSchema(output, resolvedSchema(contract.outputSchema))
        if (!validation.valid) {
          return failure('ANALYSIS_RESULT_INVALID', validation.errors.join(', '), false)
        }
        return { success: true, summary: `分析完成：${output.analysis.analysisId}`, output }
      } catch (error) {
        return failureFrom(error, 'ANALYSIS_SERVICE_FAILED')
      }
    }),

    registration('flood.showRange', async (args, context) => {
      try {
        const scene = requireScene(getScene)
        const result = await scene.assistantShowFloodRange(args.analysis)
        const undoToken = saveUndo('flood.showRange', () => scene.assistantUndoShowRange(result.snapshot), context)
        const output = {
          success: true,
          analysisId: result.analysisId,
          layerIds: result.layerIds,
          renderedFeatureCount: result.renderedFeatureCount,
          undoToken,
        }
        return { success: true, summary: `已展示 ${result.renderedFeatureCount} 个淹没范围`, output, undoToken }
      } catch (error) {
        return failureFrom(error, 'LAYER_RENDER_FAILED')
      }
    }, context => consumeUndo('flood.showRange', context)),

    registration('ui.clearAnalysis', async (args, context) => {
      try {
        const scene = requireScene(getScene)
        const result = await scene.assistantClearAnalysis(args.analysisId)
        const undoToken = saveUndo('ui.clearAnalysis', () => scene.assistantRestoreAnalysis(result.snapshot), context)
        const output = {
          success: true,
          analysisId: result.analysisId,
          clearedLayerIds: result.clearedLayerIds,
          undoToken,
        }
        return { success: true, summary: `已清除分析 ${result.analysisId}`, output, undoToken }
      } catch (error) {
        return failureFrom(error, 'CLEAR_FAILED')
      }
    }, context => consumeUndo('ui.clearAnalysis', context)),
  ]

  return { actions, undoRecords }
}

export function registerCompetitionActionsOnce(client, options) {
  const existing = registrationsByClient.get(client)
  if (existing) return existing

  const created = createCompetitionActionRegistrations(options)
  const unregister = created.actions.map(action => client.registerAction(action))
  const registration = {
    ...created,
    actionNames: created.actions.map(action => action.name),
    unregister() {
      unregister.forEach(dispose => dispose())
      registrationsByClient.delete(client)
    },
  }
  registrationsByClient.set(client, registration)
  return registration
}

export { FROZEN_ACTION_NAMES }
